import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Download, Share2, X, CloudUpload, Loader2, Info } from 'lucide-react'
import { api } from '@/lib/api'
import { getToken } from '@/lib/auth'
import type { MessageAttachment, ToolCall } from '@/types'
import { cn } from '@/lib/utils'

export interface MediaSource {
  src: string
  authenticated?: boolean
}

export interface MediaItem {
  id: string
  kind: 'image' | 'video'
  src: string
  filename: string
  mimeType?: string
  prompt?: string
  model?: string
  dimensions?: string
  sourceId?: string
  createdAt?: string
  authenticated?: boolean
  sources?: MediaSource[]
}

function mediaSources(item: MediaItem): MediaSource[] {
  const candidates = item.sources?.length
    ? item.sources
    : [{ src: item.src, authenticated: item.authenticated }]
  const seen = new Set<string>()
  return candidates.filter((candidate) => {
    if (!candidate.src || seen.has(candidate.src)) return false
    seen.add(candidate.src)
    return true
  })
}

async function fetchBlobFromSource(source: MediaSource): Promise<Blob> {
  if (source.src.startsWith('data:')) {
    const response = await fetch(source.src)
    return response.blob()
  }
  const response = await fetch(source.src, {
    headers: source.authenticated ? { Authorization: `Bearer ${getToken()}` } : undefined,
  })
  if (!response.ok) throw new Error(`Media request failed (${response.status})`)
  return response.blob()
}

async function fetchBlob(item: MediaItem): Promise<Blob> {
  const candidates = mediaSources(item)
  let lastError: Error | null = null
  for (const source of candidates) {
    try {
      return await fetchBlobFromSource(source)
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Media request failed')
    }
  }
  throw lastError || new Error('No media source available')
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error('Unable to read media'))
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '')
    reader.readAsDataURL(blob)
  })
}

function AuthenticatedMedia({
  item,
  className,
  onOpen,
}: {
  item: MediaItem
  className?: string
  onOpen?: () => void
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  const sourceSignature = useMemo(
    () => mediaSources(item).map((source) => `${source.authenticated ? 'auth' : 'public'}:${source.src}`).join('|'),
    [item],
  )

  useEffect(() => {
    let cancelled = false
    let created = ''
    setFailed(false)
    setObjectUrl(null)

    fetchBlob(item)
      .then((blob) => {
        if (cancelled) return
        created = URL.createObjectURL(blob)
        setObjectUrl(created)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [item.id, sourceSignature])

  if (failed) {
    return <div className={cn('flex min-h-24 items-center justify-center rounded-xl bg-surface-elevated px-3 text-xs text-danger', className)}>Media unavailable</div>
  }
  if (!objectUrl) {
    return <div className={cn('flex items-center justify-center bg-surface-elevated', className)}><Loader2 className="h-4 w-4 animate-spin text-text-muted" /></div>
  }

  if (item.kind === 'video') {
    return <video src={objectUrl} controls playsInline preload="metadata" className={className} onDoubleClick={onOpen} />
  }

  return <img src={objectUrl} alt={item.prompt || item.filename} className={className} onClick={onOpen} />
}

function MediaViewer({ item, onClose }: { item: MediaItem; onClose: () => void }) {
  const [busy, setBusy] = useState<'download' | 'share' | 'save' | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [showInfo, setShowInfo] = useState(false)

  const download = async () => {
    try {
      setBusy('download')
      const blob = await fetchBlob(item)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = item.filename
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Download failed')
    } finally {
      setBusy(null)
    }
  }

  const share = async () => {
    try {
      setBusy('share')
      const blob = await fetchBlob(item)
      const file = new File([blob], item.filename, { type: blob.type || item.mimeType || (item.kind === 'video' ? 'video/mp4' : 'image/png') })
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ files: [file], title: item.filename, text: item.prompt })
      } else if (navigator.share && !item.src.startsWith('data:')) {
        await navigator.share({ title: item.filename, text: item.prompt, url: item.src })
      } else {
        await navigator.clipboard.writeText(item.src.startsWith('data:') ? item.filename : item.src)
        setStatus('Media link copied')
      }
    } catch (e) {
      if (e instanceof Error && e.name !== 'AbortError') setStatus(e.message)
    } finally {
      setBusy(null)
    }
  }

  const save = async () => {
    try {
      setBusy('save')
      const blob = await fetchBlob(item)
      const data = await blobToBase64(blob)
      await api.libraryUpload(item.filename, blob.type || item.mimeType || (item.kind === 'video' ? 'video/mp4' : 'image/png'), data)
      setStatus('Saved to Library')
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusy(null)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="max-w-[96vw] max-h-[92vh] flex flex-col items-center gap-3">
        <AuthenticatedMedia item={item} className="max-w-[94vw] max-h-[80vh] object-contain rounded-xl" />
        {item.prompt && <p className="max-w-2xl text-center text-xs text-white/70">{item.prompt}</p>}
        {showInfo && (
          <div className="max-w-2xl w-full rounded-xl bg-black/55 border border-white/10 px-4 py-3 text-xs text-white/70 space-y-1.5">
            {item.sourceId && <div><span className="text-white/45">Asset:</span> {item.sourceId}</div>}
            {item.model && <div><span className="text-white/45">Model:</span> {item.model}</div>}
            {item.dimensions && <div><span className="text-white/45">Size:</span> {item.dimensions}</div>}
            {item.createdAt && <div><span className="text-white/45">Created:</span> {new Date(item.createdAt).toLocaleString()}</div>}
            {item.prompt && <div className="pt-1"><span className="text-white/45">Prompt:</span> {item.prompt}</div>}
          </div>
        )}
      </div>
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <button onClick={download} disabled={!!busy} className="h-10 w-10 rounded-xl bg-black/50 text-white/80 hover:text-white flex items-center justify-center" title="Download">
          {busy === 'download' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </button>
        <button onClick={share} disabled={!!busy} className="h-10 w-10 rounded-xl bg-black/50 text-white/80 hover:text-white flex items-center justify-center" title="Share">
          {busy === 'share' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
        </button>
        <button onClick={save} disabled={!!busy} className="h-10 w-10 rounded-xl bg-black/50 text-white/80 hover:text-white flex items-center justify-center" title="Save to Library">
          {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}
        </button>
        <button onClick={() => setShowInfo(v => !v)} className="h-10 w-10 rounded-xl bg-black/50 text-white/80 hover:text-white flex items-center justify-center" title="Image details">
          <Info className="h-4 w-4" />
        </button>
        <button onClick={onClose} className="h-10 w-10 rounded-xl bg-black/50 text-white/80 hover:text-white flex items-center justify-center" title="Close">
          <X className="h-5 w-5" />
        </button>
      </div>
      {status && <div className="absolute bottom-5 rounded-lg bg-black/70 px-3 py-2 text-xs text-white">{status}</div>}
    </div>,
    document.body,
  )
}

export function MediaGrid({ items }: { items: MediaItem[] }) {
  const [open, setOpen] = useState<MediaItem | null>(null)
  const [sharingId, setSharingId] = useState<string | null>(null)
  if (!items.length) return null

  const columns = items.length === 1 ? 'grid-cols-1' : 'grid-cols-2'

  const shareItem = async (item: MediaItem) => {
    try {
      setSharingId(item.id)
      const blob = await fetchBlob(item)
      const file = new File([blob], item.filename, {
        type: blob.type || item.mimeType || (item.kind === 'video' ? 'video/mp4' : 'image/png'),
      })

      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ files: [file], title: item.filename, text: item.prompt })
      } else if (!item.src.startsWith('data:')) {
        await navigator.clipboard.writeText(item.src)
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return
    } finally {
      setSharingId(null)
    }
  }

  return (
    <>
      <div className={cn(
        'grid gap-2 my-1',
        items.length === 1 ? 'max-w-[520px]' : 'max-w-[620px]',
        columns,
      )}>
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'group relative overflow-hidden bg-surface-elevated/30',
              items.length === 1 ? 'rounded-[1.65rem]' : 'rounded-2xl',
            )}
          >
            <AuthenticatedMedia
              item={item}
              className={cn(
                'block w-full object-cover',
                item.kind === 'image' && 'cursor-pointer',
                items.length === 1
                  ? 'max-h-[560px] object-contain'
                  : 'aspect-square',
              )}
              onOpen={() => setOpen(item)}
            />
            <button
              type="button"
              onClick={() => { void shareItem(item) }}
              disabled={sharingId === item.id}
              className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white/90 shadow-lg backdrop-blur-md transition hover:bg-black/60 disabled:opacity-60"
              title="Share"
              aria-label="Share media"
            >
              {sharingId === item.id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Share2 className="h-4 w-4" />}
            </button>
          </div>
        ))}
      </div>
      {open && <MediaViewer item={open} onClose={() => setOpen(null)} />}
    </>
  )
}

export function mediaItemsFromAttachments(attachments?: MessageAttachment[]): MediaItem[] {
  if (!attachments) return []
  return attachments
    .filter((a) => (a.kind === 'image' || a.kind === 'video') && (a.data || a.url))
    .map((a, i) => ({
      id: a.id || `${a.name}-${i}`,
      kind: a.kind as 'image' | 'video',
      src: a.data ? `data:${a.mimeType};base64,${a.data}` : a.url!,
      filename: a.name,
      mimeType: a.mimeType,
      prompt: typeof a.metadata?.prompt === 'string' ? a.metadata.prompt : undefined,
      model: typeof a.metadata?.model === 'string' ? a.metadata.model : undefined,
      dimensions: typeof a.metadata?.size === 'string' ? a.metadata.size : undefined,
      sourceId: typeof a.metadata?.sourceId === 'string' ? a.metadata.sourceId : undefined,
      createdAt: typeof a.metadata?.createdAt === 'string' ? a.metadata.createdAt : undefined,
      authenticated: !!a.url && a.url.startsWith('/api/'),
    }))
}

export function mergeMediaItems(...groups: MediaItem[][]): MediaItem[] {
  const merged = new Map<string, MediaItem>()

  for (const group of groups) {
    for (const item of group) {
      const key = item.sourceId || item.id || `${item.kind}:${item.src}`
      const itemSources = mediaSources(item)
      const existing = merged.get(key)

      if (!existing) {
        const primary = itemSources[0]
        merged.set(key, {
          ...item,
          src: primary?.src || item.src,
          authenticated: primary?.authenticated,
          sources: itemSources,
        })
        continue
      }

      const combinedSources = [...mediaSources(existing), ...itemSources]
      const seen = new Set<string>()
      const uniqueSources = combinedSources.filter((source) => {
        if (!source.src || seen.has(source.src)) return false
        seen.add(source.src)
        return true
      })
      const primary = uniqueSources[0]

      merged.set(key, {
        ...item,
        ...existing,
        prompt: existing.prompt || item.prompt,
        model: existing.model || item.model,
        dimensions: existing.dimensions || item.dimensions,
        sourceId: existing.sourceId || item.sourceId,
        createdAt: existing.createdAt || item.createdAt,
        src: primary?.src || existing.src || item.src,
        authenticated: primary?.authenticated,
        sources: uniqueSources,
      })
    }
  }

  return Array.from(merged.values())
}

export function parseToolResultObject(result?: string): Record<string, unknown> | null {
  if (!result) return null
  let current: unknown = result
  for (let i = 0; i < 3; i += 1) {
    if (current && typeof current === 'object') return current as Record<string, unknown>
    if (typeof current !== 'string') return null
    try {
      current = JSON.parse(current)
    } catch {
      return null
    }
  }
  return current && typeof current === 'object' ? current as Record<string, unknown> : null
}

export function mediaItemsFromToolCalls(toolCalls?: ToolCall[]): MediaItem[] {
  if (!toolCalls) return []
  const mediaTools = new Set(['generate_image', 'get_image', 'get_image_status', 'generate_video', 'get_video', 'get_video_status'])
  const items: MediaItem[] = []

  const addObject = (obj: Record<string, unknown>, toolName: string, index: number) => {
    const isVideo = toolName.includes('video') || String(obj.mime_type || '').startsWith('video/')
    const dataUri = typeof obj.data_uri === 'string' ? obj.data_uri : ''
    const rawServeUrl = typeof obj.serve_url === 'string' ? obj.serve_url : (typeof obj.url === 'string' ? obj.url : '')
    const ext = isVideo ? 'mp4' : 'png'
    const id = String(obj.video_id || obj.image_id || obj.job_id || `${toolName}-${index}`)
    const canonicalServeUrl = !isVideo && /^IMG-[A-Za-z0-9-]+$/.test(id)
      ? `/api/dashboard/images/${encodeURIComponent(id)}`
      : rawServeUrl

    const sources: MediaSource[] = []
    if (dataUri) sources.push({ src: dataUri, authenticated: false })
    if (canonicalServeUrl) sources.push({ src: canonicalServeUrl, authenticated: false })
    if (rawServeUrl && rawServeUrl !== canonicalServeUrl) sources.push({ src: rawServeUrl, authenticated: false })

    const src = sources[0]?.src || ''
    if (!src) return

    items.push({
      id,
      kind: isVideo ? 'video' : 'image',
      src,
      filename: `${id}.${ext}`,
      mimeType: isVideo ? 'video/mp4' : 'image/png',
      prompt: typeof obj.prompt === 'string' ? obj.prompt : undefined,
      model: typeof obj.model_used === 'string' ? obj.model_used : undefined,
      dimensions: typeof obj.size === 'string' ? obj.size : undefined,
      sourceId: id,
      createdAt: typeof obj.created_at === 'string' ? obj.created_at : undefined,
      authenticated: sources[0]?.authenticated,
      sources,
    })
  }

  toolCalls.forEach((tc, i) => {
    if (!mediaTools.has(tc.name) || tc.status !== 'success') return
    const obj = parseToolResultObject(tc.result)
    if (!obj) return
    addObject(obj, tc.name, i)
    for (const key of ['images', 'videos', 'media']) {
      const collection = obj[key]
      if (Array.isArray(collection)) {
        collection.forEach((entry, j) => {
          if (entry && typeof entry === 'object') addObject(entry as Record<string, unknown>, tc.name, i * 100 + j)
        })
      }
    }
  })

  const seen = new Set<string>()
  return items.filter((item) => {
    const key = `${item.kind}:${item.src}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
